import { Checkbox, ClassesIllustration, EmptyState } from '@bp/ui-components';
import classNames from 'classnames';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { PersonLabelSource, PersonLabelType, PersonListType } from 'components/Persons/types/personTypes';
import { personHasLabel } from 'components/Persons/utils/personUtils';
import { ReactNode, useEffect, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PersonsList.module.scss';
import { PersonsListEntry } from './PersonsListEntry';

const PersonsListFragment = graphql(`
  fragment PersonsListFields on Query {
    labels: personLabels(schoolYearUuid: $schoolyearUuid, context: $labelContext, uuids: $labelUuids) {
      __typename
      context
      items {
        __typename
        uuid
        labels {
          __typename
          uuid
          source
          sourceUuids
          value
          subValue
          tooltip
        }
      }
    }
  }
`);

type SimplePersonsListProps = {
  persons: PersonListType[];
  selectedLabels: PersonLabelType[];
  search?: string;
  activeSources: PersonLabelSource[];
  selectedPersons?: string[];
  padding?: 's' | 'm';
  showBirthdays?: boolean;
  emptyStatePadding?: 'none' | 's' | 'm' | 'l' | 'xl';
  onCountChanged?: (current: number, max: number) => void;
  onGetActions?: (uuid: string) => ReactNode;
  onLabelClick: (label: PersonLabelType) => void;
  onSelectAll?: (uuids: string[]) => void;
  onSelectCheckbox?: (uuid: string) => void;
  onSelectRow?: (uuid: string) => void;
  data: FragmentType<typeof PersonsListFragment> | undefined;
};

export const SimplePersonsList = ({
  persons,
  selectedLabels,
  search,
  activeSources,
  selectedPersons,
  padding,
  showBirthdays,
  emptyStatePadding = 'l',
  onCountChanged,
  onGetActions,
  onLabelClick,
  onSelectAll,
  onSelectCheckbox,
  onSelectRow,
  data,
}: SimplePersonsListProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(PersonsListFragment, data);

  const labels: PersonLabelType[] = useMemo(
    () =>
      (fragment?.labels.items ?? []).flatMap((person) => {
        const labels = person.labels;
        return labels.map((label) => ({
          uuid: label.uuid,
          personUuid: person.uuid,
          source: label.source,
          sourceUuids: label.sourceUuids,
          value: label.value,
          subValue: label.subValue ?? null,
          tooltip: label.tooltip ?? null,
        }));
      }),
    [fragment?.labels.items],
  );

  const uniqueSelectAllId = useId();

  const processedPersons = useMemo(() => {
    const activeSourcesSet = new Set(activeSources);

    const getLabelsForPerson = (uuid: string): PersonLabelType[] => {
      return labels.filter((label) => label.personUuid === uuid && activeSourcesSet.has(label.source)) ?? [];
    };

    const filtered =
      selectedLabels.length === 0
        ? persons
        : persons.filter((person) => {
            const personLabels = getLabelsForPerson(person.uuid);
            return selectedLabels.every((label) => personHasLabel(label, personLabels));
          });

    return filtered
      .filter((profile) => {
        if (!search || search === '') {
          return true;
        }
        const searchValue = search.toLowerCase();
        return (
          profile.listName?.toLowerCase().includes(searchValue) ||
          profile.lastName?.toLowerCase().includes(searchValue) ||
          profile.firstName?.toLowerCase().includes(searchValue) ||
          profile.email?.toLowerCase().includes(searchValue)
        );
      })
      .map((p) => {
        return {
          ...p,
          labels: getLabelsForPerson(p.uuid),
        };
      });
  }, [activeSources, labels, persons, selectedLabels, search]);

  useEffect(() => {
    onCountChanged && onCountChanged(processedPersons.length, persons.length);
  }, [processedPersons.length, onCountChanged, persons.length]);

  if (processedPersons.length === 0) {
    return (
      <EmptyState icon={<ClassesIllustration />} padding={emptyStatePadding} title={t('persons.noPerson')}></EmptyState>
    );
  }

  const actionsClasses = classNames(styles.actions, {
    [styles[`padding-${padding}`]]: padding,
  });

  return (
    <div className={styles['persons-list']}>
      <div className={styles['list-wrapper']}>
        {onSelectCheckbox || onSelectRow ? (
          <div className={actionsClasses}>
            <Checkbox
              name={`select-all-${uniqueSelectAllId}`}
              label={
                (selectedPersons ?? []).length >= processedPersons.length
                  ? t('common.deselectAll')
                  : t('common.selectAll')
              }
              checked={(selectedPersons ?? []).length >= processedPersons.length}
              onChange={() => onSelectAll && onSelectAll(processedPersons.map((p) => p.uuid))}
              className={styles['select-all']}
            />
            <div className={styles.left}>
              <div className={styles.info}>
                {t('persons.labels.selectCount', {
                  current: (selectedPersons ?? []).length,
                  max: processedPersons.length,
                })}
              </div>
            </div>
          </div>
        ) : undefined}
        {processedPersons.map((person) => {
          return (
            <PersonsListEntry
              key={person.uuid}
              personUuid={person.uuid}
              name={person.listName}
              email={person.email}
              birthday={person.birthday}
              labels={person.labels}
              actions={onGetActions && onGetActions(person.uuid)}
              status={person.userStatus}
              selected={selectedPersons?.includes(person.uuid)}
              padding={padding}
              showBirthdays={showBirthdays}
              onSelectCheckbox={onSelectCheckbox ? (uuid) => onSelectCheckbox(uuid) : undefined}
              onSelectRow={onSelectRow ? (uuid) => onSelectRow(uuid) : undefined}
              onLabelClick={(label) => onLabelClick(label)}
            />
          );
        })}
      </div>
    </div>
  );
};
